import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Stack, Alert, Skeleton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  fetchLiveSessions, enrolInLive, withdrawFromLive, LiveSession,
} from '../../services/monetizationServices';

/**
 * Live teacher-led review sessions, student side (#07).
 *
 * The Join control renders only when the server says `enrolled` — the API withholds
 * `joinUrl` otherwise, and this deliberately does not try to reconstruct it. Guessing
 * a join link client-side would put unenrolled students into a paid session.
 *
 * Hidden entirely when nothing is scheduled: an empty "upcoming sessions" heading on
 * every dashboard is noise for a feature most students will never use.
 */
const LiveSessionsPanel: React.FC<{ studentId?: string }> = ({ studentId }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<LiveSession[] | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'locked' | 'hidden'>('loading');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await fetchLiveSessions();
    if (result.state === 'ready') {
      setSessions(result.sessions);
      setState(result.sessions.length ? 'ready' : 'hidden');
    } else if (result.state === 'locked') {
      setState('locked');
    } else {
      setState('hidden');   // unavailable or not deployed: say nothing rather than error
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (state === 'loading') return <Skeleton variant="rounded" height={120} sx={{ my: 2 }} />;
  if (state === 'hidden') return null;

  if (state === 'locked') {
    return (
      <Alert
        severity="info"
        sx={{ my: 2 }}
        action={<Button onClick={() => navigate('/checkout?sku=live')}>See plans</Button>}
      >
        Live review sessions are part of a paid plan.
      </Alert>
    );
  }

  const act = async (session: LiveSession) => {
    if (!studentId) { setNotice('Choose a student first.'); return; }
    setBusyId(session.id);
    setNotice(null);
    if (session.enrolled) {
      const ok = await withdrawFromLive(session.id, studentId);
      if (!ok) setNotice('We could not release your place. Try again.');
    } else {
      const result = await enrolInLive(session.id, studentId);
      if (result === 'locked') { navigate('/checkout?sku=live'); setBusyId(null); return; }
      if (result === 'full') setNotice('That session is full.');
      if (result === 'error') setNotice('We could not enrol you just now.');
    }
    await load();
    setBusyId(null);
  };

  return (
    <Card sx={{ my: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Live review sessions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A teacher walks through a mock paper with your cohort.
        </Typography>

        {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}

        <Stack spacing={1.5}>
          {(sessions ?? []).map((s) => {
            const when = new Date(s.startsAt);
            const full = s.placesLeft != null && s.placesLeft <= 0 && !s.enrolled;
            return (
              <Box key={s.id} sx={{
                display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
                p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2,
              }}>
                <Box sx={{ flex: '1 1 220px' }}>
                  <Typography sx={{ fontWeight: 650 }}>{s.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {when.toLocaleString()} {s.teacherName ? `· ${s.teacherName}` : ''}
                    {s.durationMinutes ? ` · ${s.durationMinutes} min` : ''}
                  </Typography>
                </Box>

                {s.placesLeft != null && (
                  <Chip size="small"
                        color={full ? 'default' : 'primary'}
                        variant="outlined"
                        label={full ? 'Full' : `${s.placesLeft} places left`} />
                )}

                {/* joinUrl is present only when the server says we are enrolled. */}
                {s.enrolled && s.joinUrl && (
                  <Button variant="contained" href={s.joinUrl} target="_blank" rel="noreferrer">
                    Join
                  </Button>
                )}

                <Button
                  variant={s.enrolled ? 'text' : 'outlined'}
                  disabled={busyId === s.id || (full && !s.enrolled)}
                  onClick={() => act(s)}
                >
                  {s.enrolled ? 'Leave' : 'Enrol'}
                </Button>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default LiveSessionsPanel;
